import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Variants } from 'framer-motion';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CRMTransition from './CRMTransition';

interface CRMJourneyProps {
  isLight: boolean;
  setShowCrmDef: React.Dispatch<React.SetStateAction<boolean>>;
}

type Stage = 'problema' | 'ordenando' | 'solucion';

const phaseToStage = (phase: 0 | 1 | 2 | 3): Stage => {
  if (phase === 0) return 'problema';
  if (phase === 1) return 'ordenando';
  return 'solucion';
};

const reveal: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const textFade: Variants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] } },
};

const CRMJourney: React.FC<CRMJourneyProps> = ({ setShowCrmDef }) => {
  const { t } = useTranslation();
  const [stage, setStage] = useState<Stage>('problema');

  const STAGE_COPY: Record<Stage, {
    badgeColor: string;
    badge: string;
    heading: React.ReactNode;
    paragraph: string;
  }> = {
    problema: {
      badgeColor: '#EF4444',
      badge: t('journey.problem_badge', 'El Problema'),
      heading: (
        <>
          {t('journey.problem_h1', 'Sin orden,')} <br />
          {t('journey.problem_h2', 'tus clientes')}{' '}
          <span className="grad-text" style={{ fontWeight: 800 }}>{t('journey.problem_h3', 'se evaporan')}</span>
        </>
      ),
      paragraph: t('journey.problem_p', 'Así se ve el día a día sin un CRM: información dispersa, seguimientos olvidados y ventas que se enfrían por falta de control.'),
    },
    ordenando: {
      badgeColor: '#F59E0B',
      badge: t('journey.sorting_badge', 'Ordenando'),
      heading: (
        <>
          Ruray Digital <br />
          {t('journey.sorting_h1', 'pone')} <span className="grad-text" style={{ fontWeight: 800 }}>{t('journey.sorting_h2', 'todo en su lugar')}</span>
        </>
      ),
      paragraph: t('journey.sorting_p', 'Cada mensaje, nota y recordatorio encuentra su sitio en segundos — sin que muevas un dedo.'),
    },
    solucion: {
      badgeColor: '#8B5CF6',
      badge: t('journey.solution_badge', 'La Solución'),
      heading: (
        <>
          {t('journey.solution_h1', 'Del caos al orden absoluto con')} <br />
          <span className="grad-text" style={{ fontWeight: 800 }}>Ruray Digital</span>
        </>
      ),
      paragraph: t('journey.solution_p', 'Centralizamos cada interacción, automatizamos las tareas repetitivas y te damos el control total sobre tu crecimiento comercial.'),
    },
  };

  const copy = STAGE_COPY[stage];

  return (
    <section className="crm-panel">
      <div className="container journey-container" style={{ position: 'relative' }}>
        <div className="journey-layout-grid">
          {/* Columna de texto: cambia junto con la animación */}
          <motion.div
            className="journey-text-column"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            variants={reveal}
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'left' }}
          >
            <AnimatePresence mode="wait">
              <motion.div key={stage} initial="initial" animate="animate" exit="exit" variants={textFade}>
                <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '1.5rem' }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: '9px 20px',
                    background: copy.badgeColor,
                    color: '#000',
                    borderRadius: '999px', fontSize: '0.9rem',
                    fontWeight: 800,
                  }}>
                    {copy.badge}
                  </div>
                </div>

                <h2 style={{ fontSize: 'clamp(2.15rem, 4vw, 3.8rem)', marginBottom: '1.5rem', fontWeight: 300, letterSpacing: '-0.04em', lineHeight: 1.1, color: 'var(--text-primary)' }}>
                  {copy.heading}
                </h2>

                <p style={{ color: 'var(--text-secondary)', fontSize: '1.12rem', maxWidth: '460px', marginBottom: '2.5rem', lineHeight: 1.5, fontWeight: 400 }}>
                  {copy.paragraph}
                </p>

                {stage === 'problema' && (
                  <button
                    onClick={() => setShowCrmDef(true)}
                    className="btn-crm-info"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '12px 28px',
                      fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
                      width: 'fit-content',
                    }}
                  >
                    <Info size={20} color="currentColor" />
                    {t('journey.what_is_crm_btn', '¿Qué es un CRM?')}
                  </button>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* Columna de animación: caos → pipeline → dashboard, en un solo tablero */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            variants={reveal}
            className="journey-board-column"
          >
            <CRMTransition onPhaseChange={(phase) => setStage(phaseToStage(phase))} />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CRMJourney;
